(() => {
  'use strict';
  if (customElements.get('snd-cctv-collection')) return;

  const PREFIX = 'cctv_';
  const clean = value => String(value == null ? '' : value).trim().replace(/\s+/g, ' ');
  const normalise = value => clean(value).toLocaleLowerCase('en');
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  let cartQueue = Promise.resolve();

  class CctvCollection extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.lifecycle = new AbortController();
      this.ready = false;
      this.loading = false;
      this.groups = new Map();
      this.selected = new Map();
      this.config = JSON.parse(this.dataset.cctvConfig);
      this.text = this.config.text;
      this.grid = this.querySelector('[data-grid]');
      this.panel = this.querySelector('[data-panel]');
      this.dialog = this.querySelector('[data-dialog]');
      this.sidebar = this.querySelector('[data-sidebar]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.page = 1;
      this.pageSize = Math.max(6, Number(this.config.pageSize) || 24);
      this.pageCards = new Map([[Number(this.config.currentPage), Array.from(this.grid.querySelectorAll('[data-cctv-card]'))]]);

      this.querySelectorAll('[data-filter-group]').forEach(node => {
        const key = node.dataset.key;
        if (!/^(type|vendor|collections|tags|meta_[A-Za-z0-9_-]+)$/.test(key) || this.groups.has(key)) { node.hidden = true; return; }
        this.groups.set(key, { node, key, label: node.dataset.label, values: new Map(), options: [] });
      });
      const on = (target, event, handler) => target.addEventListener(event, handler, { signal: this.lifecycle.signal });
      on(this, 'change', event => this.handleChange(event));
      on(this, 'click', event => this.handleClick(event));
      on(this, 'submit', event => {
        if (!event.target.matches('[data-cctv-cart-form]')) return;
        event.preventDefault();
        this.addToCart(event.target);
      });
      on(this.querySelector('[data-sort-form]'), 'submit', event => { event.preventDefault(); this.sort(); });
      on(window, 'popstate', () => { if (this.ready) { this.readState(); this.render(); } });
      on(this.mobile, 'change', () => { if (!this.mobile.matches) this.closeDrawer(); });
      on(this.dialog, 'close', () => this.restorePanel());
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDrawer();
      });
      this.loadProducts();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.closeDrawer();
      this.started = false;
    }

    async fetchPage(page) {
      const url = new URL(window.location.href);
      url.hash = '';
      Array.from(url.searchParams.keys()).forEach(key => { if (key.startsWith(PREFIX)) url.searchParams.delete(key); });
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.config.sectionId);
      url.searchParams.set(this.config.pageParam || 'page', page);
      const request = new AbortController();
      const abort = () => request.abort();
      this.lifecycle.signal.addEventListener('abort', abort, { once: true });
      const timeout = window.setTimeout(abort, 20000);
      try {
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Could not load collection page.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = doc.querySelector('snd-cctv-collection');
        if (!root) throw new Error('Collection section missing.');
        const config = JSON.parse(root.dataset.cctvConfig);
        if (Number(config.currentPage) !== page || Number(config.total) !== Number(this.config.total)) throw new Error('Collection changed during loading. Reload the page.');
        const cards = Array.from(root.querySelectorAll('[data-cctv-card]'));
        if (!cards.length && Number(this.config.total) > 0) throw new Error('Collection page is empty.');
        return cards;
      } finally {
        window.clearTimeout(timeout);
        this.lifecycle.signal.removeEventListener('abort', abort);
      }
    }

    async loadProducts() {
      if (this.loading) return;
      this.loading = true;
      const status = this.querySelector('[data-filter-status]');
      status.hidden = false;
      status.textContent = this.text.loading;
      this.querySelector('[data-load-error]').hidden = true;
      try {
        const pages = Math.max(1, Number(this.config.pages) || 1);
        for (let page = 1; page <= pages; page += 1) {
          if (!this.pageCards.has(page)) this.pageCards.set(page, await this.fetchPage(page));
          if (!this.isConnected || this.lifecycle.signal.aborted) return;
        }
        const seen = new Set();
        const nodes = Array.from(this.pageCards.entries()).sort((a, b) => a[0] - b[0]).flatMap(entry => entry[1]);
        this.products = nodes.map(node => {
          const data = JSON.parse(node.dataset.cctvProduct);
          const specs = {};
          this.groups.forEach((group, key) => {
            const values = Array.isArray(data.specs[key]) ? data.specs[key] : [];
            specs[key] = Array.from(new Set(values.map(normalise).filter(Boolean)));
            if (group) values.forEach(value => {
              const text = clean(value);
              if (text && !group.values.has(normalise(text))) group.values.set(normalise(text), text);
            });
          });
          return { node, id: String(data.id), specs };
        }).filter(product => {
          if (seen.has(product.id)) return false;
          seen.add(product.id);
          return true;
        });
        if (this.products.length !== Number(this.config.total)) throw new Error('The collection changed. Please reload this page.');
        this.readState();
        this.buildOptions();
        this.grid.replaceChildren(...this.products.map(product => product.node));
        this.querySelector('[data-filter-fields]').disabled = false;
        this.querySelector('[data-open]').disabled = false;
        const hasOptions = Array.from(this.groups.values()).some(group => group.values.size);
        status.textContent = hasOptions ? '' : this.text.browse;
        status.hidden = hasOptions;
        this.querySelector('[data-open]').hidden = !hasOptions;
        this.querySelector('[data-server-pagination]')?.setAttribute('hidden', '');
        this.ready = true;
        this.render();
      } catch (error) {
        if (this.lifecycle.signal.aborted) return;
        status.textContent = this.text.unavailable;
        this.querySelector('[data-error-message]').textContent = this.text.load_error;
        this.querySelector('[data-load-error]').hidden = false;
      } finally {
        this.loading = false;
      }
    }

    readState() {
      const params = new URL(window.location.href).searchParams;
      this.selected.clear();
      this.groups.forEach(group => {
        const values = params.getAll(PREFIX + group.key).map(clean).filter(Boolean);
        if (values.length) {
          this.selected.set(group.key, new Set(values.map(normalise)));
          values.forEach(value => { if (!group.values.has(normalise(value))) group.values.set(normalise(value), value); });
        }
      });
      this.page = Math.max(1, parseInt(params.get(PREFIX + 'page'), 10) || 1);
      if (this.ready) this.buildOptions();
    }

    buildOptions() {
      this.groups.forEach(group => {
        const holder = group.node.querySelector('[data-options]');
        holder.replaceChildren();
        group.options = [];
        group.node.hidden = group.values.size === 0;
        Array.from(group.values.entries()).sort((a, b) => {
          return collator.compare(a[1], b[1]);
        }).forEach(([value, text]) => {
          const label = document.createElement('label');
          label.className = 'snd-cctv__option';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.name = PREFIX + group.key;
          input.value = value;
          input.dataset.filterKey = group.key;
          const name = document.createElement('span');
          name.className = 'snd-cctv__option-label';
          name.textContent = text;
          const count = document.createElement('span');
          count.className = 'snd-cctv__option-count';
          count.setAttribute('aria-hidden', 'true');
          label.append(input, name, count);
          holder.append(label);
          group.options.push({ value, label, input, count });
        });
      });
    }

    matches(product, exceptKey) {
      for (const [key, values] of this.selected) {
        if (key !== exceptKey && values.size && !product.specs[key].some(value => values.has(value))) return false;
      }
      return true;
    }

    render() {
      const matches = this.products.filter(product => this.matches(product));
      const totalPages = Math.max(1, Math.ceil(matches.length / this.pageSize));
      this.page = Math.min(this.page, totalPages);
      const start = (this.page - 1) * this.pageSize;
      const visible = new Set(matches.slice(start, start + this.pageSize));
      this.products.forEach(product => { product.node.hidden = !visible.has(product); });
      const total = matches.length;
      this.querySelector('[data-result-count]').textContent = total > this.pageSize
        ? `${start + 1}–${Math.min(start + this.pageSize, total)} ${this.text.of} ${total} ${this.text.products}`
        : `${total} ${total === 1 ? this.text.product : this.text.products}`;
      this.querySelector('[data-show-results]').textContent = `${this.text.show} ${total} ${total === 1 ? this.text.product : this.text.products}`;
      this.querySelector('[data-empty]').hidden = total > 0;

      this.groups.forEach(group => {
        const counts = new Map();
        this.products.forEach(product => {
          if (!this.matches(product, group.key)) return;
          product.specs[group.key].forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
        });
        group.options.forEach(option => {
          const checked = this.selected.get(group.key)?.has(option.value) || false;
          const count = counts.get(option.value) || 0;
          option.input.checked = checked;
          option.input.disabled = count === 0 && !checked;
          option.count.textContent = `(${count})`;
          option.label.classList.toggle('is-unavailable', count === 0 && !checked);
          option.label.classList.toggle('is-selected', checked);
        });
      });
      this.renderChips();
      this.renderPagination(totalPages);
    }

    renderChips() {
      const holder = this.querySelector('[data-active-filters]');
      holder.replaceChildren();
      let total = 0;
      this.selected.forEach((values, key) => {
        const group = this.groups.get(key);
        values.forEach(value => {
          total += 1;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'snd-cctv__chip';
          button.dataset.removeKey = key;
          button.dataset.removeValue = value;
          const label = `${group.label}: ${group.values.get(value) || value}`;
          button.setAttribute('aria-label', `${this.text.remove} ${label}`);
          button.append(document.createTextNode(label));
          const cross = document.createElement('span');
          cross.textContent = '×';
          cross.setAttribute('aria-hidden', 'true');
          button.append(cross);
          holder.append(button);
        });
      });
      holder.hidden = total === 0;
      this.querySelectorAll('[data-clear]').forEach(button => { button.hidden = total === 0; });
      const badge = this.querySelector('[data-selection-count]');
      badge.hidden = total === 0;
      badge.textContent = total;
    }

    renderPagination(totalPages) {
      const nav = this.querySelector('[data-client-pagination]');
      nav.replaceChildren();
      nav.hidden = totalPages < 2;
      if (totalPages < 2) return;
      const add = (page, text, disabled = false) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = text;
        button.dataset.page = page;
        button.disabled = disabled;
        if (String(page) === text) {
          button.setAttribute('aria-label', `${this.text.page} ${page}`);
          if (page === this.page) button.setAttribute('aria-current', 'page');
        }
        nav.append(button);
      };
      add(this.page - 1, this.text.previous, this.page === 1);
      const pages = new Set([1, totalPages, this.page - 1, this.page, this.page + 1]);
      let previous = 0;
      Array.from(pages).filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b).forEach(page => {
        if (previous && page - previous > 1) { const dots = document.createElement('span'); dots.textContent = '…'; nav.append(dots); }
        add(page, String(page));
        previous = page;
      });
      add(this.page + 1, this.text.next, this.page === totalPages);
    }

    writeState() {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach(key => { if (key.startsWith(PREFIX)) url.searchParams.delete(key); });
      url.searchParams.delete(this.config.pageParam || 'page');
      this.selected.forEach((values, key) => values.forEach(value => url.searchParams.append(PREFIX + key, this.groups.get(key).values.get(value) || value)));
      if (this.page > 1) url.searchParams.set(PREFIX + 'page', this.page);
      if (url.href !== window.location.href) window.history.pushState({}, '', url);
    }

    handleChange(event) {
      if (event.target.matches('[data-cart-variant]')) { this.updateVariant(event.target); return; }
      if (event.target.matches('[data-sort]')) { this.sort(); return; }
      const input = event.target.closest('[data-filter-key]');
      if (!this.ready || !input) return;
      const key = input.dataset.filterKey;
      if (!this.selected.has(key)) this.selected.set(key, new Set());
      const values = this.selected.get(key);
      if (input.checked) values.add(input.value); else values.delete(input.value);
      if (!values.size) this.selected.delete(key);
      this.page = 1;
      this.render();
      this.writeState();
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-open')) { this.openDrawer(); return; }
      if (button.hasAttribute('data-close')) { this.closeDrawer(); return; }
      if (button.hasAttribute('data-retry')) { this.loadProducts(); return; }
      if (!this.ready) return;
      if (button.hasAttribute('data-clear')) {
        this.selected.clear();
        this.page = 1;
      } else if (button.hasAttribute('data-remove-key')) {
        const values = this.selected.get(button.dataset.removeKey);
        values?.delete(button.dataset.removeValue);
        if (!values?.size) this.selected.delete(button.dataset.removeKey);
        this.page = 1;
      } else if (button.hasAttribute('data-page')) {
        this.page = Number(button.dataset.page);
      } else return;
      const pageClick = button.hasAttribute('data-page');
      const removedChip = button.hasAttribute('data-remove-key');
      this.render();
      this.writeState();
      if (pageClick || removedChip || (!this.dialog.open && button.hidden)) {
        const target = this.querySelector(pageClick ? '[data-results]' : '[data-result-count]');
        target.tabIndex = -1;
        target.focus({ preventScroll: true });
        if (pageClick) target.scrollIntoView({ block: 'start', behavior: 'instant' });
      } else if (this.dialog.open && button.hidden) {
        this.querySelector('[data-close]').focus();
      }
    }

    sort() {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.querySelector('[data-sort]').value);
      url.searchParams.delete(this.config.pageParam || 'page');
      url.searchParams.delete(PREFIX + 'page');
      window.location.assign(url.href);
    }

    updateVariant(select) {
      const option = select.selectedOptions[0];
      if (!option?.value) return;
      const card = select.closest('[data-cctv-card]');
      card.querySelector('[data-card-price]').textContent = option.dataset.price;
      card.querySelector('[data-card-from]').hidden = true;
      const compare = card.querySelector('[data-card-compare]');
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
      const sku = card.querySelector('[data-card-sku]');
      if (sku) { sku.textContent = option.dataset.sku ? `${this.text.sku} ${option.dataset.sku}` : ''; sku.hidden = !option.dataset.sku; }
      select.form.querySelector('[data-cart-feedback]').hidden = true;
    }

    cartMessage(form, message, isError = false, showCart = false) {
      form.querySelector('[data-cart-feedback]').hidden = false;
      const status = form.querySelector('[data-cart-message]');
      status.textContent = message;
      status.classList.toggle('is-error', isError);
      form.querySelector('[data-view-cart]').hidden = !showCart;
    }

    async cartRequest(url, options = {}) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { ...options, credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
        const data = await response.json().catch(() => null);
        return { response, data };
      } finally {
        window.clearTimeout(timeout);
      }
    }

    async refreshCart() {
      const cartUrl = this.config.cartUrl || window.Theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      const { response, data } = await this.cartRequest(`${cartUrl}.js`, { cache: 'no-store' });
      if (!response.ok || !Number.isInteger(data?.item_count)) throw new Error('Cart count unavailable.');
      document.querySelectorAll('[data-header-cart-count]').forEach(badge => {
        badge.dataset.headerCartCount = data.item_count > 0 ? String(data.item_count) : '';
        badge.classList.toggle('visible', data.item_count > 0);
      });
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: data }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    }

    addToCart(form) {
      if (form.dataset.adding === 'true' || !form.reportValidity()) return;
      const payload = new FormData(form);
      if (!payload.get('id')) return;
      const button = form.querySelector('[data-add-to-cart]');
      const label = button.querySelector('[data-add-label]');
      const select = form.querySelector('[data-cart-variant]');
      form.dataset.adding = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (select) select.disabled = true;
      label.textContent = this.text.adding;
      form.querySelector('[data-cart-feedback]').hidden = true;

      const task = async () => {
        try {
          if (!form.isConnected) return;
          const url = new URL(form.action, window.location.href);
          url.pathname = url.pathname.replace(/\/$/, '') + '.js';
          const { response, data } = await this.cartRequest(url.href, { method: 'POST', body: payload });
          if (!response.ok) {
            if (response.status >= 400 && response.status < 500) {
              const message = typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : this.text.cart_error;
              this.cartMessage(form, message, true, true);
              await this.refreshCart().catch(() => {});
              return;
            }
            throw new Error('The add could not be confirmed.');
          }
          if (!data?.id && !data?.items?.length) throw new Error('The add could not be confirmed.');
          this.cartMessage(form, this.text.added, false, true);
          try {
            await this.refreshCart();
          } catch (error) {
            this.cartMessage(form, this.text.added_count_error, false, true);
          }
        } catch (error) {
          this.cartMessage(form, this.text.uncertain_cart, true, true);
        } finally {
          delete form.dataset.adding;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (select) select.disabled = false;
          label.textContent = this.text.add_to_cart;
        }
      };
      cartQueue = cartQueue.then(task, task);
    }

    openDrawer() {
      if (!this.mobile.matches || !this.ready || this.dialog.open) return;
      this.dialog.append(this.panel);
      this.dialog.showModal();
      document.documentElement.classList.add('snd-cctv-scroll-lock');
      this.querySelector('[data-open]').setAttribute('aria-expanded', 'true');
      this.panel.querySelector('[data-close]').focus();
    }

    closeDrawer() {
      if (this.dialog?.open) this.dialog.close();
      this.restorePanel();
    }

    restorePanel() {
      if (this.panel && this.sidebar && this.panel.parentElement !== this.sidebar) this.sidebar.append(this.panel);
      document.documentElement.classList.remove('snd-cctv-scroll-lock');
      this.querySelector('[data-open]')?.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('snd-cctv-collection', CctvCollection);
})();
